import { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { meetsAccessLevel, type RouteAccessLevel } from '@/lib/routeAccess'

interface ProtectedRouteProps {
  children?: React.ReactNode
  requiredAccessLevel?: RouteAccessLevel
}

export function ProtectedRoute({ children, requiredAccessLevel }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, dbUser } = useAuth()
  const location = useLocation()

  // `dbUser &&` keeps this failing OPEN while syncCurrentUser is still in
  // flight — bouncing a legitimate user off the page they asked for would be
  // worse than a brief render. The sidebar fails closed instead; see
  // canAccessRoute.
  const insufficientAccess =
    requiredAccessLevel &&
    dbUser &&
    !meetsAccessLevel(dbUser.accessLevel, requiredAccessLevel)

  useEffect(() => {
    if (insufficientAccess) {
      toast.error('Ei käyttöoikeutta tälle sivulle')
    }
  }, [insufficientAccess])

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Ladataan...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (insufficientAccess) {
    return <Navigate to="/dashboard" replace />
  }

  return children ? <>{children}</> : <Outlet />
}

export default ProtectedRoute
